import { createActions } from 'redux-actions';
import normalize from 'json-api-normalize';
import get from 'lodash/get';
import { addFlashMessage, clearMessage } from '../Flash';
import { addLoaderMessage } from '../Loader';
import { fetchUserProjects } from '../Dashboard';
import { sendUpload, getErrorMessages } from '../../../shared/helpers';
import api, { removeToken, saveToken } from '../../../api';

export const {
  authenticationSuccess,
  getUserSuccess,
  createAccountSuccess,
  updateAccountSuccess,
  setAuthenticationTab,
  forgotPasswordSuccess,
  resetPasswordTokenSuccess,
  resetPasswordSuccess,
  confirmEmailSuccess,
  resetPasswordRequest,
  resetPasswordRequestFailure,
  forgotPasswordRequest,
  forgotPasswordRequestFailure,
  resetPasswordTokenRequest,
  resetPasswordTokenRequestFailure,
  authenticationRequest,
  authenticationRequestFailure,
  authenticationLogout,
  getUserRequest,
  getUserRequestFailure,
  createAccountRequest,
  createAccountRequestFailure,
  updateAccountRequest,
  updateAccountRequestFailure,
  changePasswordRequest,
  changePasswordRequestFailure,
  changePasswordSuccess,
  confirmEmailRequest,
  confirmEmailFailure,
  getTrackFailure,
  getTrackSuccess,
  updateTrackFailure,
  updateTrackSuccess,
} = createActions(
  {
    AUTHENTICATION_SUCCESS: data => ({ data }),
    GET_USER_SUCCESS: data => ({ data }),
    CREATE_ACCOUNT_SUCCESS: data => ({ data }),
    UPDATE_ACCOUNT_SUCCESS: data => ({ data }),
    SET_AUTHENTICATION_TAB: data => ({ data }),
    FORGOT_PASSWORD_SUCCESS: data => ({ data }),
    RESET_PASSWORD_TOKEN_SUCCESS: data => ({ data }),
    RESET_PASSWORD_SUCCESS: data => ({ data }),
    CONFIRM_EMAIL_SUCCESS: data => ({ data }),
  },
  'RESET_PASSWORD_REQUEST',
  'RESET_PASSWORD_REQUEST_FAILURE',
  'FORGOT_PASSWORD_REQUEST',
  'FORGOT_PASSWORD_REQUEST_FAILURE',
  'RESET_PASSWORD_TOKEN_REQUEST',
  'RESET_PASSWORD_TOKEN_REQUEST_FAILURE',
  'AUTHENTICATION_REQUEST',
  'AUTHENTICATION_REQUEST_FAILURE',
  'AUTHENTICATION_LOGOUT',
  'GET_USER_REQUEST',
  'GET_USER_REQUEST_FAILURE',
  'CREATE_ACCOUNT_REQUEST',
  'CREATE_ACCOUNT_REQUEST_FAILURE',
  'UPDATE_ACCOUNT_REQUEST',
  'UPDATE_ACCOUNT_REQUEST_FAILURE',
  'CHANGE_PASSWORD_REQUEST',
  'CHANGE_PASSWORD_REQUEST_FAILURE',
  'CHANGE_PASSWORD_SUCCESS',
  'CONFIRM_EMAIL_REQUEST',
  'CONFIRM_EMAIL_FAILURE',
  'GET_TRACK_FAILURE',
  'GET_TRACK_SUCCESS',
  'UPDATE_TRACK_FAILURE',
  'UPDATE_TRACK_SUCCESS',
);

export const login = data => dispatch => {
  dispatch(clearMessage());
  dispatch(authenticationRequest());

  return api
    .login(data)
    .then(response => response.data)
    .then(json => {
      if (json.errors) {
        dispatch(addFlashMessage(json.errors, 'error'));

        return dispatch(authenticationRequestFailure());
      }
      saveToken(json);
      dispatch(addFlashMessage([`Welcome back!`], 'success'));
      dispatch(fetchUser());
      dispatch(fetchUserProjects());

      return dispatch(authenticationSuccess(json));
    })
    .catch(e => {
      const errors = getErrorMessages(e);
      dispatch(addFlashMessage(errors, 'error'));

      return dispatch(authenticationRequestFailure());
    });
};

export const loginByToken = data => dispatch => {
  dispatch(clearMessage());
  dispatch(authenticationRequest());

  saveToken(data);
  dispatch(addFlashMessage([`Welcome back!`], 'success'));
  dispatch(fetchUser());
  dispatch(fetchUserProjects());

  return dispatch(authenticationSuccess(data));
};

export const forgotPassword = data => dispatch => {
  dispatch(clearMessage());
  dispatch(forgotPasswordRequest());

  return api
    .forgotPassword(data)
    .then(response => response.data)
    .then(json => {
      if (json.errors) {
        dispatch(addFlashMessage(json.errors, 'error'));

        return dispatch(forgotPasswordRequestFailure());
      }
      dispatch(
        addFlashMessage(
          ['An email with reset instructions was sent to your account'],
          'success',
        ),
      );

      return dispatch(forgotPasswordSuccess(json));
    })
    .catch(e => {
      const errors = getErrorMessages(e);
      dispatch(addFlashMessage(errors, 'error'));

      return dispatch(forgotPasswordRequestFailure());
    });
};

export const resetPasswordToken = data => dispatch => {
  dispatch(clearMessage());
  dispatch(resetPasswordTokenRequest());

  saveToken(data);

  return api
    .resetPasswordToken(data)
    .then(response => response.data)
    .then(json => {
      if (json.errors) {
        dispatch(addFlashMessage(json.errors, 'error'));

        return dispatch(resetPasswordTokenRequestFailure());
      }

      return dispatch(resetPasswordTokenSuccess(json));
    })
    .catch(e => {
      const errors = getErrorMessages(e);
      dispatch(addFlashMessage(errors, 'error'));

      return dispatch(resetPasswordTokenRequestFailure());
    });
};

export const changePassword = data => dispatch => {
  dispatch(clearMessage());
  dispatch(changePasswordRequest());

  return api
    .changePassword(data)
    .then(response => response.data)
    .then(json => {
      if (json.errors) {
        dispatch(addFlashMessage(json.errors, 'error'));

        return dispatch(changePasswordRequestFailure());
      }
      dispatch(
        addFlashMessage(
          ['Your password has been changed successfully!'],
          'success',
        ),
      );

      return dispatch(changePasswordSuccess());
    })
    .catch(e => {
      const errors = getErrorMessages(e);
      dispatch(addFlashMessage(errors, 'error'));
      return dispatch(changePasswordRequestFailure());
    });
};

export const resetPassword = data => dispatch => {
  dispatch(clearMessage());
  dispatch(resetPasswordRequest());

  return api
    .resetPassword(data)
    .then(response => response.data)
    .then(json => {
      if (json.errors) {
        dispatch(addFlashMessage(json.errors, 'error'));

        return dispatch(resetPasswordRequestFailure());
      }
      dispatch(
        addFlashMessage(
          ['Your password has been reset successfully!'],
          'success',
        ),
      );

      return dispatch(resetPasswordSuccess(json));
    })
    .catch(e => {
      const errors = getErrorMessages(e);
      dispatch(addFlashMessage(errors, 'error'));
      return dispatch(resetPasswordRequestFailure());
    });
};

export const createAccount = data => dispatch => {
  dispatch(clearMessage());
  dispatch(createAccountRequest());

  return api
    .createAccount(data)
    .then(response => response.data)
    .then(json => {
      if (json.errors) {
        dispatch(addFlashMessage(json.errors, 'error'));

        return dispatch(createAccountRequestFailure());
      }
      const normalized = normalize(json).get([
        'token',
        'id',
        'email',
        'name',
        'phone_number',
        'company',
        'tasks.id',
        'projects.id',
      ]);
      dispatch(createAccountSuccess(normalized));
      return dispatch(loginByToken(json.payload));
    })
    .catch(e => {
      const errors = getErrorMessages(e);
      dispatch(addFlashMessage(errors, 'error'));
      return dispatch(createAccountRequestFailure());
    });
};

export const updateAccount = formData => dispatch => {
  dispatch(clearMessage());
  dispatch(addLoaderMessage('Saving...'));
  dispatch(updateAccountRequest());

  if (get(formData, 'avatar_url', {}) instanceof File) {
    return sendUpload(formData.avatar_url)
      .then(url => {
        const parsedFormData = {
          ...formData,
          avatar_url: url,
        };
        return updateUser(parsedFormData, dispatch);
      })
      .catch(e => {
        dispatch(clearMessage());
        const errors = getErrorMessages(e);
        dispatch(addFlashMessage(errors, 'error'));
        return dispatch(updateAccountRequestFailure());
      });
  }
  return updateUser(formData, dispatch);
};

const updateUser = (userData, dispatch) =>
  api
    .updateUser(userData)
    .then(response => response.data)
    .then(json => {
      if (json.errors) {
        dispatch(addFlashMessage(json.errors, 'error'));

        return dispatch(updateAccountRequestFailure());
      }
      return dispatch(updateAccountSuccess(json));
    })
    .catch(e => {
      const errors = getErrorMessages(e);
      dispatch(addFlashMessage(errors, 'error'));
      return dispatch(updateAccountRequestFailure());
    });

export const getTrack = () => dispatch =>
  api
    .getTrack()
    .then(response => response.data)
    .then(json => {
      if (json.errors) {
        dispatch(addFlashMessage(json.errors, 'error'));

        return dispatch(getTrackFailure());
      }
      return dispatch(getTrackSuccess(json));
    })
    .catch(e => {
      const errors = getErrorMessages(e);
      dispatch(addFlashMessage(errors, 'error'));
      return dispatch(getTrackFailure());
    });

export const updateTrack = data => dispatch =>
  api
    .updateTrack(data)
    .then(response => response.data)
    .then(json => {
      if (json.errors) {
        dispatch(addFlashMessage(json.errors, 'error'));

        return dispatch(updateTrackFailure());
      }
      return dispatch(updateTrackSuccess(json));
    })
    .catch(e => {
      const errors = getErrorMessages(e);
      dispatch(addFlashMessage(errors, 'error'));
      return dispatch(updateTrackFailure());
    });

export const logout = () => dispatch => {
  dispatch(clearMessage());
  dispatch(authenticationLogout());
  removeToken();
};

export const authenticationTab = activeTab => dispatch => {
  dispatch(clearMessage());
  dispatch(setAuthenticationTab(activeTab));
};

export const fetchUser = () => dispatch => {
  dispatch(clearMessage());
  dispatch(getUserRequest());

  return api
    .fetchUser()
    .then(response => response.data)
    .then(json => {
      if (json.errors) {
        dispatch(addFlashMessage(json.errors, 'error'));
        return dispatch(logout());
      }
      return dispatch(getUserSuccess(json));
    })
    .catch(e => {
      const errors = getErrorMessages(e);
      dispatch(addFlashMessage(errors, 'error'));
      dispatch(getUserRequestFailure());
    });
};

// eslint-disable-next-line camelcase
export const confirmEmail = confirmation_token => dispatch => {
  dispatch(clearMessage());
  dispatch(confirmEmailRequest());

  return (
    api
      // eslint-disable-next-line prettier/prettier
      .confirmEmail({ confirmation_token })
      .then(response => response.data)
      .then(json => {
        if (json.errors) {
          dispatch(addFlashMessage(json.errors, 'error'));
          return dispatch(confirmEmailFailure());
        }
        dispatch(addFlashMessage(json.message, 'success'));
        return dispatch(confirmEmailSuccess(json));
      })
      .catch(e => {
        const errors = getErrorMessages(e);
        dispatch(addFlashMessage(errors, 'error'));
        return dispatch(confirmEmailFailure());
      })
  );
};



// WEBPACK FOOTER //
// ./src/store/actions/Authentication/index.js